import {
  ExtractedTaskResult,
  ImportActionPayload,
} from '../../../types/actionImportTypes'
import { TypeAction, status as STATUS_VALUES } from '../../../utils/constants'
import { roundTo, toNumber } from '../../../utils/formatNumbers'

/** "1 500,50" → "1500.5"; unparseable text is kept so validation can flag it. */
export function normalizeCost(raw: string): string {
  if (!raw) return ''
  const n = toNumber(raw.replace(/[^\d.,\s\u00A0\u202F'’+-]/g, ''))
  return n === null ? raw : String(roundTo(n, 2))
}

/** Context shared by the wizard (step 2), bulk edit and the row editor. */
export type ImportContext = {
  MissionId: string
  ProjetId: string
  SourceAudit: string[]
  SourceIncident: string[]
  SourceRisque: string[]
  SecurityIds: string[]
  SecurityControlIds: string[]
  SubDomainIds: string[]
}

export type ContextField = keyof ImportContext

export const CONTEXT_FIELDS: ContextField[] = [
  'MissionId',
  'ProjetId',
  'SourceAudit',
  'SourceIncident',
  'SourceRisque',
  'SecurityIds',
  'SecurityControlIds',
  'SubDomainIds',
]

export const EMPTY_CONTEXT: ImportContext = {
  MissionId: '',
  ProjetId: '',
  SourceAudit: [],
  SourceIncident: [],
  SourceRisque: [],
  SecurityIds: [],
  SecurityControlIds: [],
  SubDomainIds: [],
}

/** Context prefilled from the screen that opened the wizard. */
export function contextFromIds(missionId?: string, projectId?: string): ImportContext {
  return {
    ...EMPTY_CONTEXT,
    MissionId: missionId || '',
    ProjetId: missionId ? projectId || '' : '',
  }
}

export type ImportRow = ImportContext & {
  id: string
  origin: 'extracted' | 'manual'
  /** 1-based position in the extracted file, for "row 12" references. */
  sourceLine?: number
  ActionName: string
  Description: string
  /** yyyy-mm-dd, or the raw text when it cannot be parsed. */
  DateDebut: string
  DateFin: string
  /** Kept as text so a bad value from the file stays visible and editable. */
  Cout: string
  Priority: number | null
  TypeAction: string
  Status: string
  /** Raw values from the file that could not be mapped (shown in error messages). */
  raw: Partial<Record<'Priority' | 'TypeAction' | 'Status', string>>
  /** Last error returned by POST /action-import/import for this row. */
  serverError?: string
}

export type RowField =
  | 'ActionName'
  | 'Description'
  | 'DateDebut'
  | 'DateFin'
  | 'Cout'
  | 'Priority'
  | 'TypeAction'
  | 'Status'
  | 'MissionId'
  | 'ProjetId'
  | 'Sources'
  | 'SecurityIds'
  | 'SecurityControlIds'
  | 'SubDomainIds'
  | 'server'

/** i18n key + interpolation values, translated at render time. */
export type FieldError = { key: string; values?: Record<string, string> }

export type RowErrors = Partial<Record<RowField, FieldError>>

/* Same lookup tables as grc-back/src/action-import/action-import.mapper.ts */
const PRIORITY_MAP: Record<string, number> = {
  critical: 1,
  high: 2,
  medium: 3,
  low: 4,
}

const STATUS_MAP: Record<string, string> = {
  'to do': 'A faire',
  todo: 'A faire',
  'a faire': 'A faire',
  'à faire': 'A faire',
  'in progress': 'En cours',
  'en cours': 'En cours',
  done: 'Terminé',
  terminé: 'Terminé',
  terminée: 'Terminé',
  suspended: 'Suspendu',
  suspendu: 'Suspendu',
  waiting: 'En attente',
  pending: 'En attente',
  'on hold': 'En attente',
  'en attente': 'En attente',
}

const TYPE_MAP: Record<string, string> = {
  governance: 'Gouvernance',
  gouvernance: 'Gouvernance',
  organizational: 'Organisationnel',
  organisationnel: 'Organisationnel',
  operational: 'Opérationnel',
  opérationnel: 'Opérationnel',
  physical: 'Physique',
  physique: 'Physique',
  it: 'IT',
}

const key = (value: unknown) => String(value ?? '').trim().toLowerCase()

const text = (value: unknown) =>
  value === null || value === undefined ? '' : String(value).trim()

export function newRowId(prefix = 'row'): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`
}

export function parseDateValue(value: string): Date | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

export function toIsoDay(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Normalize a date-ish value to yyyy-mm-dd; keep unparseable text as-is. */
function normalizeDate(value: unknown): string {
  const raw = text(value)
  if (!raw) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
  const parsed = parseDateValue(raw)
  return parsed ? toIsoDay(parsed) : raw
}

export function rowFromExtracted(
  result: ExtractedTaskResult,
  context: ImportContext
): ImportRow {
  const o = result.original || ({} as ExtractedTaskResult['original'])
  const priorityRaw = text(o.priority)
  const typeRaw = text(o.type)
  const statusRaw = text(o.status)

  return {
    ...cloneContext(context),
    id: newRowId('x'),
    origin: 'extracted',
    sourceLine: result.index + 1,
    ActionName: text(o.task_name),
    Description: text(o.description),
    DateDebut: normalizeDate(o.start_date),
    DateFin: normalizeDate(o.end_date),
    Cout: normalizeCost(text(o.initial_cost)),
    Priority: PRIORITY_MAP[key(priorityRaw)] ?? null,
    TypeAction: TYPE_MAP[key(typeRaw)] ?? '',
    Status: STATUS_MAP[key(statusRaw)] ?? '',
    raw: {
      ...(priorityRaw && PRIORITY_MAP[key(priorityRaw)] === undefined
        ? { Priority: priorityRaw }
        : {}),
      ...(typeRaw && TYPE_MAP[key(typeRaw)] === undefined
        ? { TypeAction: typeRaw }
        : {}),
      ...(statusRaw && STATUS_MAP[key(statusRaw)] === undefined
        ? { Status: statusRaw }
        : {}),
    },
  }
}

export function emptyRow(context: ImportContext): ImportRow {
  const today = toIsoDay(new Date())
  return {
    ...cloneContext(context),
    id: newRowId('m'),
    origin: 'manual',
    ActionName: '',
    Description: '',
    DateDebut: today,
    DateFin: today,
    Cout: '0',
    Priority: 3,
    TypeAction: 'IT',
    Status: 'A faire',
    raw: {},
  }
}

export function cloneContext(context: ImportContext): ImportContext {
  return {
    MissionId: context.MissionId,
    ProjetId: context.ProjetId,
    SourceAudit: [...context.SourceAudit],
    SourceIncident: [...context.SourceIncident],
    SourceRisque: [...context.SourceRisque],
    SecurityIds: [...context.SecurityIds],
    SecurityControlIds: [...context.SecurityControlIds],
    SubDomainIds: [...context.SubDomainIds],
  }
}

export function pickContext(row: ImportContext): ImportContext {
  return cloneContext(row)
}

const sameValue = (a: string | string[], b: string | string[]) =>
  Array.isArray(a) && Array.isArray(b)
    ? a.length === b.length && a.every((v, i) => v === b[i])
    : a === b

const isEmptyValue = (v: string | string[]) =>
  Array.isArray(v) ? v.length === 0 : !v

/**
 * When the user changes the step-2 defaults after rows exist, update only the
 * context fields that still hold the previous default (or are empty), so
 * per-row edits are never overwritten.
 */
export function applyContextChange(
  row: ImportRow,
  previous: ImportContext,
  next: ImportContext
): ImportRow {
  let changed = false
  const updated: ImportRow = { ...row }
  CONTEXT_FIELDS.forEach((field) => {
    const current = row[field]
    if (sameValue(previous[field], next[field])) return
    if (isEmptyValue(current) || sameValue(current, previous[field])) {
      ;(updated as any)[field] = Array.isArray(next[field])
        ? [...(next[field] as string[])]
        : next[field]
      changed = true
    }
  })
  return changed ? updated : row
}

export function validateRow(row: ImportRow): RowErrors {
  const errors: RowErrors = {}
  const required = { key: 'actionImport.errors.required' }

  if (!row.ActionName.trim()) errors.ActionName = required
  if (!row.Description.trim()) errors.Description = required

  const start = parseDateValue(row.DateDebut)
  const end = parseDateValue(row.DateFin)
  if (!row.DateDebut) errors.DateDebut = required
  else if (!start)
    errors.DateDebut = {
      key: 'actionImport.errors.invalidDate',
      values: { value: row.DateDebut },
    }
  if (!row.DateFin) errors.DateFin = required
  else if (!end)
    errors.DateFin = {
      key: 'actionImport.errors.invalidDate',
      values: { value: row.DateFin },
    }
  if (start && end && end < start)
    errors.DateFin = { key: 'actionImport.errors.endBeforeStart' }

  if (row.Cout === '') errors.Cout = required
  else {
    const cost = toNumber(row.Cout)
    if (cost === null || cost < 0)
      errors.Cout = {
        key: 'actionImport.errors.invalidCost',
        values: { value: row.Cout },
      }
  }

  if (row.Priority === null || ![1, 2, 3, 4].includes(row.Priority))
    errors.Priority = row.raw.Priority
      ? {
          key: 'actionImport.errors.unknownValue',
          values: { value: row.raw.Priority },
        }
      : required
  if (!TypeAction.includes(row.TypeAction))
    errors.TypeAction = row.raw.TypeAction
      ? {
          key: 'actionImport.errors.unknownValue',
          values: { value: row.raw.TypeAction },
        }
      : required
  if (!STATUS_VALUES.includes(row.Status))
    errors.Status = row.raw.Status
      ? {
          key: 'actionImport.errors.unknownValue',
          values: { value: row.raw.Status },
        }
      : required

  if (!row.MissionId) errors.MissionId = required
  if (
    row.ProjetId &&
    !row.SourceAudit.length &&
    !row.SourceIncident.length &&
    !row.SourceRisque.length
  )
    errors.Sources = { key: 'actionImport.errors.projectNeedsSource' }
  if (!row.SecurityIds.length) errors.SecurityIds = required
  if (!row.SecurityControlIds.length) errors.SecurityControlIds = required
  if (!row.SubDomainIds.length) errors.SubDomainIds = required

  if (row.serverError)
    errors.server = {
      key: 'actionImport.errors.server',
      values: { message: row.serverError },
    }

  return errors
}

export function toImportPayload(row: ImportRow): ImportActionPayload {
  return {
    ActionName: row.ActionName.trim(),
    Description: row.Description.trim(),
    TypeAction: row.TypeAction,
    DateDebut: row.DateDebut,
    DateFin: row.DateFin,
    Priority: Number(row.Priority),
    Cout: roundTo(toNumber(row.Cout) ?? 0, 2),
    Status: row.Status,
    MissionId: row.MissionId,
    ...(row.ProjetId ? { ProjetId: row.ProjetId } : {}),
    SecurityIds: row.SecurityIds,
    SecurityControlIds: row.SecurityControlIds,
    SubDomainIds: row.SubDomainIds,
    ...(row.SourceAudit.length ? { SourceAudit: row.SourceAudit } : {}),
    ...(row.SourceIncident.length ? { SourceIncident: row.SourceIncident } : {}),
    ...(row.SourceRisque.length ? { SourceRisque: row.SourceRisque } : {}),
  }
}

/** Grid column → error key, for per-cell indicators and "jump to error". */
export const FIELD_ORDER: RowField[] = [
  'ActionName',
  'Description',
  'DateDebut',
  'DateFin',
  'Cout',
  'Priority',
  'TypeAction',
  'Status',
  'MissionId',
  'ProjetId',
  'Sources',
  'SecurityIds',
  'SecurityControlIds',
  'SubDomainIds',
  'server',
]
